import { useNavigate } from 'react-router-dom';

import type { ActiveSessionConflict } from '@perplexicon/api-client';

import { Button } from './Button.js';
import { Dialog } from './Dialog.js';
import { ErrorState } from './ErrorState.js';
import { getErrorMessage } from '../lib/errors.js';

export interface ActiveGameConflictDialogProps {
  /** The blocking session, or `null` to keep the dialog closed. */
  conflict: ActiveSessionConflict | null;
  onAbandon: () => void;
  onDismiss: () => void;
  isAbandoning: boolean;
  abandonError?: unknown;
}

/**
 * Shown when the server refuses a start/join with `ACTIVE_SESSION_EXISTS`
 * (APP_SPEC §21.1): the user already has a live session of this game in
 * this slot, and has to either go back to it or give it up.
 *
 * The consequence of abandoning is spelled out rather than implied,
 * because the two cases differ sharply: giving up a private solo run costs
 * nothing anyone else can see, while abandoning a game with real opponents
 * forfeits it — they win, and it is recorded against the user. `status`
 * distinguishes the third case, a multiplayer game that never started, in
 * which case nothing is forfeited at all.
 *
 * No game-specific text appears here (CLAUDE.md §2); the game's name comes
 * from the server's conflict details.
 */
export function ActiveGameConflictDialog({
  conflict,
  onAbandon,
  onDismiss,
  isAbandoning,
  abandonError,
}: ActiveGameConflictDialogProps) {
  const navigate = useNavigate();
  if (!conflict) return null;

  const neverStarted = conflict.status === 'pending';
  const forfeits = !neverStarted && conflict.hasOpponents;

  const description = neverStarted
    ? `You have a ${conflict.gameName} game waiting for someone to accept. You can only have one at a time.`
    : conflict.hasOpponents
      ? `You are already playing ${conflict.gameName} against someone else. You can only have one game of each kind going at a time.`
      : `You have an unfinished ${conflict.gameName} game. You can only have one at a time.`;

  const consequence = neverStarted
    ? 'Giving it up cancels the invitation. Nothing is recorded against you.'
    : forfeits
      ? 'Giving it up forfeits the game: your opponents win, and it is recorded as a forfeit.'
      : 'Giving it up ends that game. Your progress in it is lost.';

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onDismiss();
      }}
      title="You already have a game in progress"
      description={description}
    >
      <p className="font-editorial text-sm text-textSecondary">{consequence}</p>

      {abandonError !== undefined && abandonError !== null && (
        <div className="mt-4">
          <ErrorState message={getErrorMessage(abandonError)} />
        </div>
      )}

      <div className="mt-6 flex flex-col gap-2 tablet:flex-row-reverse">
        <Button
          onClick={() => {
            onDismiss();
            void navigate(`/play/${conflict.sessionId}`);
          }}
        >
          Back to my game
        </Button>
        <Button variant="danger" isLoading={isAbandoning} onClick={onAbandon}>
          {neverStarted ? 'Cancel it and continue' : 'Abandon it and continue'}
        </Button>
        <Button variant="ghost" disabled={isAbandoning} onClick={onDismiss}>
          Never mind
        </Button>
      </div>
    </Dialog>
  );
}
